/**
 * LSDN Fragrance Quiz — scoring engine
 * Requires: assets/quiz-data.js (window.LsdnQuizData) loaded first
 */
(function (global) {
  'use strict';

  const STORAGE_KEY = 'lsdn_quiz_pair_history';
  const STORAGE_MAX_ENTRIES = 50;
  const STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  const SCORE_EPSILON = 1e-6;

  const QUESTION_IDS = {
    Q1: 'how-do-you-want-to-feel',
    Q2: 'your-presence-usually-feels',
    Q5: 'fragrance-world',
  };

  /** @typedef {{ questionId: string, answerId: string }} NormalizedAnswer */
  /** @typedef {{ fragranceId: string, points: number }} RawScore */

  // ---------------------------------------------------------------------------
  // Data access
  // ---------------------------------------------------------------------------

  function getQuizData() {
    const data = global.LsdnQuizData;
    if (!data || !data.questions || !data.fragrances) {
      throw new Error('LsdnQuizData is not loaded. Include quiz-data.js before lsdn-quiz-engine.js.');
    }
    return data;
  }

  function getMeta(data) {
    return data.meta || {};
  }

  function getFragrance(data, fragranceId) {
    const fragrance = data.fragrances[fragranceId];
    if (!fragrance) {
      throw new Error(`Unknown fragrance id: ${fragranceId}`);
    }
    return fragrance;
  }

  // ---------------------------------------------------------------------------
  // Answer normalization
  // ---------------------------------------------------------------------------

  /**
   * @param {Record<string, string> | string[]} answers
   * @returns {NormalizedAnswer[]}
   */
  function normalizeAnswers(answers) {
    const data = getQuizData();
    const questions = [...data.questions].sort((a, b) => a.order - b.order);

    if (Array.isArray(answers)) {
      if (answers.length !== questions.length) {
        throw new Error(`Expected ${questions.length} answers, received ${answers.length}.`);
      }
      return questions.map((question, index) => ({
        questionId: question.id,
        answerId: String(answers[index]),
      }));
    }

    if (answers && typeof answers === 'object') {
      return questions.map((question) => {
        const answerId =
          answers[question.id] ??
          answers[String(question.order)] ??
          answers[`q${question.order}`];

        if (answerId == null || answerId === '') {
          throw new Error(`Missing answer for question "${question.id}".`);
        }

        return { questionId: question.id, answerId: String(answerId) };
      });
    }

    throw new Error('answers must be an object keyed by question id or an ordered array.');
  }

  function findAnswerDefinition(question, answerId) {
    const match = question.answers.find((a) => a.id === answerId);
    if (!match) {
      throw new Error(`Unknown answer "${answerId}" for question "${question.id}".`);
    }
    return match;
  }

  // ---------------------------------------------------------------------------
  // Weighted scoring
  // ---------------------------------------------------------------------------

  /**
   * @param {NormalizedAnswer[]} normalizedAnswers
   */
  function buildScoreState(normalizedAnswers) {
    const data = getQuizData();
    const meta = getMeta(data);
    const questionWeights = meta.questionWeights || {};
    const fragranceIds = data.fragranceIds || Object.keys(data.fragrances);

    /** @type {Record<string, number>} */
    const totals = Object.fromEntries(fragranceIds.map((id) => [id, 0]));

    /** @type {Record<string, {
     *   questionId: string,
     *   order: number,
     *   label: string,
     *   weight: number,
     *   answerId: string,
     *   answerLabel: string,
     *   contributions: Record<string, number>,
     *   rawContributions: Record<string, number>
     * }>} */
    const byQuestion = {};

    for (const { questionId, answerId } of normalizedAnswers) {
      const question = data.questions.find((q) => q.id === questionId);
      if (!question) {
        throw new Error(`Unknown question id: ${questionId}`);
      }

      const answer = findAnswerDefinition(question, answerId);
      const weight = question.weight ?? questionWeights[questionId] ?? 1;

      /** @type {Record<string, number>} */
      const contributions = Object.fromEntries(fragranceIds.map((id) => [id, 0]));
      /** @type {Record<string, number>} */
      const rawContributions = Object.fromEntries(fragranceIds.map((id) => [id, 0]));

      for (const { fragranceId, points } of answer.scores) {
        if (!totals.hasOwnProperty(fragranceId)) {
          throw new Error(`Score references unknown fragrance "${fragranceId}".`);
        }
        const weighted = points * weight;
        rawContributions[fragranceId] += points;
        contributions[fragranceId] += weighted;
        totals[fragranceId] += weighted;
      }

      byQuestion[questionId] = {
        questionId,
        order: question.order,
        label: question.label,
        weight,
        answerId,
        answerLabel: answer.label,
        contributions,
        rawContributions,
      };
    }

    const ranked = buildRankedList(totals, byQuestion, meta);

    return { totals, byQuestion, ranked, normalizedAnswers };
  }

  function buildRankedList(totals, byQuestion, meta) {
    const luxuryOrder = meta.tiebreaker?.luxuryProfileOrder || [];

    return Object.entries(totals)
      .map(([fragranceId, total]) => ({
        fragranceId,
        total,
        tiebreaker: {
          q1: byQuestion[QUESTION_IDS.Q1]?.contributions[fragranceId] ?? 0,
          q2: byQuestion[QUESTION_IDS.Q2]?.contributions[fragranceId] ?? 0,
          q5: byQuestion[QUESTION_IDS.Q5]?.contributions[fragranceId] ?? 0,
          luxuryRank: luxuryRank(fragranceId, luxuryOrder),
        },
      }))
      .sort((a, b) => compareRankedEntries(a, b));
  }

  function luxuryRank(fragranceId, luxuryOrder) {
    const index = luxuryOrder.indexOf(fragranceId);
    return index === -1 ? luxuryOrder.length : index;
  }

  /**
   * Tiebreaker order: total → Q1 weighted → Q2 weighted → Q5 weighted → luxuryProfileOrder
   */
  function compareRankedEntries(a, b) {
    if (!scoresEqual(a.total, b.total)) {
      return b.total - a.total;
    }
    if (!scoresEqual(a.tiebreaker.q1, b.tiebreaker.q1)) {
      return b.tiebreaker.q1 - a.tiebreaker.q1;
    }
    if (!scoresEqual(a.tiebreaker.q2, b.tiebreaker.q2)) {
      return b.tiebreaker.q2 - a.tiebreaker.q2;
    }
    if (!scoresEqual(a.tiebreaker.q5, b.tiebreaker.q5)) {
      return b.tiebreaker.q5 - a.tiebreaker.q5;
    }
    return a.tiebreaker.luxuryRank - b.tiebreaker.luxuryRank;
  }

  function scoresEqual(a, b) {
    return Math.abs(a - b) < SCORE_EPSILON;
  }

  // ---------------------------------------------------------------------------
  // Primary selection
  // ---------------------------------------------------------------------------

  function selectPrimary(scoreState, meta) {
    const { ranked } = scoreState;
    const winner = ranked[0];
    const tied = ranked.filter((entry) => scoresEqual(entry.total, winner.total));

    let tiebreakerStep = null;
    if (tied.length > 1) {
      if (!scoresEqual(tied[0].tiebreaker.q1, tied[1].tiebreaker.q1)) {
        tiebreakerStep = 'q1_weighted';
      } else if (!scoresEqual(tied[0].tiebreaker.q2, tied[1].tiebreaker.q2)) {
        tiebreakerStep = 'q2_weighted';
      } else if (!scoresEqual(tied[0].tiebreaker.q5, tied[1].tiebreaker.q5)) {
        tiebreakerStep = 'q5_weighted';
      } else {
        tiebreakerStep = 'luxury_profile_order';
      }
    }

    return {
      fragranceId: winner.fragranceId,
      selection: {
        method: 'weighted_total_with_tiebreaker',
        totalScore: winner.total,
        tiedCandidates: tied.map((t) => t.fragranceId),
        tiebreakerStep,
        tiebreakerPriority: meta.tiebreaker?.priority || [],
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Usage / pairing helpers
  // ---------------------------------------------------------------------------

  function getUsageType(data, fragranceId) {
    const { usageByType } = data;
    if (usageByType?.day?.includes(fragranceId)) return 'day';
    if (usageByType?.night?.includes(fragranceId)) return 'night';
    return 'flexible';
  }

  function isHeavyPair(meta, idA, idB) {
    const pairs = meta.secondaryMatch?.heavyPairsToLimit || [];
    return pairs.some(
      ([a, b]) => (a === idA && b === idB) || (a === idB && b === idA)
    );
  }

  function isSimilarProfile(meta, idA, idB) {
    const groups = meta.secondaryMatch?.similarProfiles || [];
    return groups.some(
      (group) => group.fragranceIds.includes(idA) && group.fragranceIds.includes(idB)
    );
  }

  function getCompatibilityRules(data, primaryId) {
    return data.compatibilityMatrix?.[primaryId] || null;
  }

  // ---------------------------------------------------------------------------
  // Duplicate prevention (localStorage hooks)
  // ---------------------------------------------------------------------------

  function readPairHistory() {
    if (typeof global.localStorage === 'undefined') {
      return [];
    }
    try {
      const raw = global.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const cutoff = Date.now() - STORAGE_TTL_MS;
      return parsed.filter((entry) => entry && entry.at >= cutoff);
    } catch {
      return [];
    }
  }

  function writePairHistory(entries) {
    if (typeof global.localStorage === 'undefined') {
      return;
    }
    try {
      global.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(entries.slice(-STORAGE_MAX_ENTRIES))
      );
    } catch {
      /* quota / privacy mode — non-fatal */
    }
  }

  function getRecentPairs() {
    return readPairHistory();
  }

  /**
   * Call after displaying results to feed duplicate-prevention rotation.
   * @param {string} primaryId
   * @param {string} secondaryId
   */
  function recordQuizPair(primaryId, secondaryId) {
    const history = readPairHistory();
    history.push({ primaryId, secondaryId, at: Date.now() });
    writePairHistory(history);
  }

  function countRecentPair(history, primaryId, secondaryId) {
    return history.filter(
      (entry) => entry.primaryId === primaryId && entry.secondaryId === secondaryId
    ).length;
  }

  function getRotationRule(meta, primaryId) {
    const rules = meta.secondaryMatch?.rotation?.frequentPairs || [];
    return rules.find((rule) => rule.primaryId === primaryId) || null;
  }

  // ---------------------------------------------------------------------------
  // Secondary selection (NOT plain second-highest)
  // ---------------------------------------------------------------------------

  function computeDayNightAdjustment(data, meta, primaryId, candidateId) {
    const primaryUsage = getUsageType(data, primaryId);
    const candidateUsage = getUsageType(data, candidateId);
    const adjustments = [];

    let delta = 0;

    if (primaryUsage === 'night') {
      if (candidateUsage === 'flexible') {
        delta += 4;
        adjustments.push({ rule: 'night_primary_prefers_flexible', delta: 4 });
      } else if (candidateUsage === 'night') {
        delta += 1.5;
        adjustments.push({ rule: 'night_primary_elegant_night_ok', delta: 1.5 });
      } else if (candidateUsage === 'day') {
        delta -= 3;
        adjustments.push({ rule: 'night_primary_avoids_day', delta: -3 });
      }
    } else if (primaryUsage === 'day') {
      if (candidateUsage === 'flexible') {
        delta += 2.5;
        adjustments.push({ rule: 'day_primary_prefers_flexible', delta: 2.5 });
      } else if (candidateUsage === 'day') {
        delta += 0.5;
        adjustments.push({ rule: 'day_primary_day_ok', delta: 0.5 });
      } else if (candidateUsage === 'night') {
        delta -= 1.5;
        adjustments.push({ rule: 'day_primary_soft_penalty_night', delta: -1.5 });
      }
    } else {
      if (candidateUsage === 'flexible') {
        delta += 1;
        adjustments.push({ rule: 'flexible_primary_flexible_harmony', delta: 1 });
      }
    }

    if (primaryUsage === 'night' && candidateUsage === 'night' && isHeavyPair(meta, primaryId, candidateId)) {
      delta -= 8;
      adjustments.push({ rule: 'two_heavy_night_pair', delta: -8 });
    }

    return { delta, adjustments };
  }

  function computeCompatibilityAdjustment(data, rules, candidateId) {
    const adjustments = [];
    let delta = 0;

    if (!rules) {
      return { delta, adjustments, eligible: true };
    }

    if (rules.avoidSecondIds?.includes(candidateId)) {
      return {
        delta: -1000,
        adjustments: [{ rule: 'compatibility_avoid', delta: -1000 }],
        eligible: false,
      };
    }

    if (rules.allowedSecondIds?.includes(candidateId)) {
      delta += 6;
      adjustments.push({ rule: 'compatibility_allowed', delta: 6 });
    } else {
      delta -= 4;
      adjustments.push({ rule: 'compatibility_not_in_allowed_list', delta: -4 });
    }

    return { delta, adjustments, eligible: true };
  }

  function computeRotationAdjustment(meta, primaryId, candidateId, history) {
    const adjustments = [];
    let delta = 0;
    const rule = getRotationRule(meta, primaryId);

    if (!rule) {
      return { delta, adjustments };
    }

    const overusedCount = countRecentPair(history, primaryId, rule.overusedSecondId);

    if (candidateId === rule.overusedSecondId) {
      const penalty = overusedCount >= 2 ? -6 : overusedCount >= 1 ? -3 : -1;
      delta += penalty;
      adjustments.push({
        rule: 'rotation_deprioritize_overused_pair',
        delta: penalty,
        recentCount: overusedCount,
      });
    }

    if (rule.alternativeSecondIds?.includes(candidateId) && overusedCount >= 1) {
      delta += 3;
      adjustments.push({ rule: 'rotation_boost_alternative', delta: 3 });
    }

    return { delta, adjustments };
  }

  function computeSimilarProfileAdjustment(meta, primaryId, candidateId) {
    if (!isSimilarProfile(meta, primaryId, candidateId)) {
      return { delta: 0, adjustments: [] };
    }
    return {
      delta: -3,
      adjustments: [{ rule: 'similar_profile_group_penalty', delta: -3 }],
    };
  }

  function buildSecondaryCandidatePool(data, primaryId, ranked, rules) {
    const allIds = ranked.map((r) => r.fragranceId).filter((id) => id !== primaryId);

    if (rules?.allowedSecondIds?.length) {
      const allowed = rules.allowedSecondIds.filter((id) => id !== primaryId);
      const pool = allowed.length ? allowed : allIds;
      return { pool, strategy: 'compatibility_matrix_allowed' };
    }

    const filtered = allIds.filter((id) => !rules?.avoidSecondIds?.includes(id));
    return {
      pool: filtered.length ? filtered : allIds,
      strategy: rules ? 'score_filtered_without_matrix' : 'score_ranked_all',
    };
  }

  function selectSecondary(scoreState, primaryId, meta, data) {
    const { ranked, totals, byQuestion } = scoreState;
    const rules = getCompatibilityRules(data, primaryId);
    const history = getRecentPairs();
    const secondHighest = ranked.find((r) => r.fragranceId !== primaryId) || null;

    const { pool, strategy } = buildSecondaryCandidatePool(data, primaryId, ranked, rules);

    /** @type {Array<{
     *   fragranceId: string,
     *   baseScore: number,
     *   compositeScore: number,
     *   adjustments: Array<{ rule: string, delta: number, [key: string]: unknown }>,
     *   eligible: boolean
     * }>} */
    const evaluations = [];

    for (const candidateId of pool) {
      if (candidateId === primaryId) continue;

      const baseScore = totals[candidateId] ?? 0;
      const adjustments = [];
      let compositeScore = baseScore;
      let eligible = true;

      const compat = computeCompatibilityAdjustment(data, rules, candidateId);
      compositeScore += compat.delta;
      adjustments.push(...compat.adjustments);
      if (!compat.eligible) eligible = false;

      const dayNight = computeDayNightAdjustment(data, meta, primaryId, candidateId);
      compositeScore += dayNight.delta;
      adjustments.push(...dayNight.adjustments);

      if (isHeavyPair(meta, primaryId, candidateId)) {
        compositeScore -= 10;
        adjustments.push({ rule: 'heavy_pair_prevention', delta: -10 });
      }

      const similar = computeSimilarProfileAdjustment(meta, primaryId, candidateId);
      compositeScore += similar.delta;
      adjustments.push(...similar.adjustments);

      const rotation = computeRotationAdjustment(meta, primaryId, candidateId, history);
      compositeScore += rotation.delta;
      adjustments.push(...rotation.adjustments);

      evaluations.push({
        fragranceId: candidateId,
        baseScore,
        compositeScore,
        adjustments,
        eligible,
      });
    }

    const eligibleEvaluations = evaluations.filter((e) => e.eligible);
    const sorted = [...(eligibleEvaluations.length ? eligibleEvaluations : evaluations)].sort(
      (a, b) => {
        if (!scoresEqual(a.compositeScore, b.compositeScore)) {
          return b.compositeScore - a.compositeScore;
        }
        const rankA = ranked.find((r) => r.fragranceId === a.fragranceId);
        const rankB = ranked.find((r) => r.fragranceId === b.fragranceId);
        return compareRankedEntries(rankA, rankB);
      }
    );

    const winner = sorted[0];
    if (!winner) {
      throw new Error('Unable to determine secondary fragrance.');
    }

    const rejectedSecondHighest = secondHighest
      ? {
          fragranceId: secondHighest.fragranceId,
          totalScore: secondHighest.total,
          wasPlainSecondHighest: secondHighest.fragranceId === winner.fragranceId,
          rejectedBecause:
            secondHighest.fragranceId !== winner.fragranceId
              ? 'compatibility_emotional_composite_scoring'
              : null,
        }
      : null;

    return {
      fragranceId: winner.fragranceId,
      selection: {
        method: 'compatibility_emotional_composite',
        strategy,
        candidatePool: pool,
        evaluations: sorted,
        rejectedSecondHighest,
        compatibilityRules: rules,
        recentPairHistoryCount: history.length,
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Profile label
  // ---------------------------------------------------------------------------

  const PROFILE_LABEL_RULES = [
  {
      id: 'magnetic_sophisticate',
      label: 'Magnetic Sophisticate',
      labelFr: 'Sophistication Magnétique',
      match: ({ q1Id, primaryId, primaryUsage }) =>
        ['magnetic', 'sophisticated', 'mysterious'].includes(q1Id) &&
        (primaryUsage === 'night' || ['midnight-call', 'pear-addiction', 'oud-rose'].includes(primaryId)),
    },
    {
      id: 'dark_sensuality',
      label: 'Dark Sensuality',
      labelFr: 'Sensualité Mystérieuse',
      match: ({ q1Id, primaryId, primaryUsage }) =>
        (['mysterious', 'addictive', 'powerful'].includes(q1Id) && primaryUsage === 'night') ||
        ['midnight-call', 'clandestine-rendez-vous', 'cuir-noir'].includes(primaryId),
    },
    {
      id: 'radiant_elegance',
      label: 'Radiant Elegance',
      labelFr: 'Élégance Rayonnante',
      match: ({ q1Id, primaryId, primaryUsage }) =>
        (['fresh', 'joyful', 'free'].includes(q1Id) && primaryUsage === 'day') ||
        ['citrus-poetry', 'neon-splash', 'call-me-iris'].includes(primaryId),
    },
    {
      id: 'modern_addictive_energy',
      label: 'Modern Addictive Energy',
      labelFr: 'Énergie Addictive Moderne',
      match: ({ q1Id, primaryId }) =>
        ['addictive', 'joyful', 'magnetic'].includes(q1Id) ||
        ['pear-addiction', 'red-berries-elixir', 'forbidden-nectar'].includes(primaryId),
    },
    {
      id: 'quiet_luxury_presence',
      label: 'Quiet Luxury Presence',
      labelFr: 'Présence au Luxe Discret',
      match: ({ q1Id, primaryId }) =>
        ['elegant', 'comforted', 'sophisticated'].includes(q1Id) ||
        ['call-me-iris', 'nomad-soul', 'mitsio-vanille'].includes(primaryId),
    },
    {
      id: 'warm_magnetic_soul',
      label: 'Warm Magnetic Soul',
      labelFr: 'Âme Chaleureuse et Magnétique',
      match: ({ q1Id, primaryUsage }) =>
        ['comforted', 'magnetic', 'addictive'].includes(q1Id) || primaryUsage === 'flexible',
    },
    {
      id: 'artistic_freedom',
      label: 'Artistic Freedom',
      labelFr: 'Liberté Créative',
      match: ({ q1Id, primaryId }) =>
        q1Id === 'free' || ['bohemian-absinthe', 'nomad-soul'].includes(primaryId),
    },
    {
      id: 'timeless_seduction',
      label: 'Timeless Seduction',
      labelFr: 'Séduction Intemporelle',
      match: ({ q1Id, primaryId }) =>
        ['powerful', 'sophisticated', 'mysterious'].includes(q1Id) ||
        ['oud-rose', 'saffron-flower', 'amazing-jade'].includes(primaryId),
    },
  ];

  function isFrenchLocale() {
    if (typeof document === 'undefined') return false;
    return (document.documentElement.lang || '').toLowerCase().startsWith('fr');
  }

  function resolveProfileLabel(rule) {
    return isFrenchLocale() && rule.labelFr ? rule.labelFr : rule.label;
  }

  function deriveProfileLabel(scoreState, primaryId, secondaryId, data) {
    const q1 = scoreState.byQuestion[QUESTION_IDS.Q1];
    const q1Id = q1?.answerId || '';
    const primaryUsage = getUsageType(data, primaryId);
    const secondaryUsage = getUsageType(data, secondaryId);

    const context = { q1Id, primaryId, secondaryId, primaryUsage, secondaryUsage };

    for (const rule of PROFILE_LABEL_RULES) {
      if (rule.match(context)) {
        return { label: resolveProfileLabel(rule), ruleId: rule.id };
      }
    }

    const examples = data.meta?.profileLabels?.examples || [];
    return {
      label: isFrenchLocale() ? 'Sophistication Magnétique' : (examples[0] || 'Your Signature Match'),
      ruleId: 'fallback',
    };
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * @param {Record<string, string> | string[]} answers
   * @returns {{
   *   profileLabel: string,
   *   profileLabelRuleId: string,
   *   primary: object,
   *   secondary: object,
   *   breakdown: object
   * }}
   */
  function calculateQuizResults(answers) {
    const data = getQuizData();
    const meta = getMeta(data);
    const normalizedAnswers = normalizeAnswers(answers);
    const scoreState = buildScoreState(normalizedAnswers);

    const primaryResult = selectPrimary(scoreState, meta);
    const secondaryResult = selectSecondary(
      scoreState,
      primaryResult.fragranceId,
      meta,
      data
    );

    const primary = getFragrance(data, primaryResult.fragranceId);
    const secondary = getFragrance(data, secondaryResult.fragranceId);
    const profile = deriveProfileLabel(
      scoreState,
      primary.id,
      secondary.id,
      data
    );

    const rankedWithNames = scoreState.ranked.map((entry, index) => ({
      rank: index + 1,
      fragranceId: entry.fragranceId,
      name: getFragrance(data, entry.fragranceId).name,
      total: roundScore(entry.total),
      tiebreaker: {
        q1Weighted: roundScore(entry.tiebreaker.q1),
        q2Weighted: roundScore(entry.tiebreaker.q2),
        q5Weighted: roundScore(entry.tiebreaker.q5),
        luxuryRank: entry.tiebreaker.luxuryRank,
      },
      contributionsByQuestion: Object.fromEntries(
        Object.entries(scoreState.byQuestion).map(([questionId, q]) => [
          questionId,
          roundScore(q.contributions[entry.fragranceId] ?? 0),
        ])
      ),
    }));

    return {
      profileLabel: profile.label,
      profileLabelRuleId: profile.ruleId,
      primary: { ...primary },
      secondary: { ...secondary },
      breakdown: {
        version: data.version,
        answers: normalizedAnswers.map(({ questionId, answerId }) => {
          const q = data.questions.find((item) => item.id === questionId);
          const a = findAnswerDefinition(q, answerId);
          return {
            questionId,
            questionLabel: q.label,
            answerId,
            answerLabel: a.label,
          };
        }),
        totals: mapValues(scoreState.totals, roundScore),
        byQuestion: mapValues(scoreState.byQuestion, (q) => ({
          questionId: q.questionId,
          order: q.order,
          label: q.label,
          weight: q.weight,
          answerId: q.answerId,
          answerLabel: q.answerLabel,
          contributions: mapValues(q.contributions, roundScore),
          rawContributions: mapValues(q.rawContributions, roundScore),
        })),
        ranked: rankedWithNames,
        primarySelection: {
          ...primaryResult.selection,
          fragranceId: primary.id,
          fragranceName: primary.name,
          totalScore: roundScore(primaryResult.selection.totalScore),
        },
        secondarySelection: {
          ...secondaryResult.selection,
          fragranceId: secondary.id,
          fragranceName: secondary.name,
          note: 'Secondary is chosen via compatibility + emotional balancing, not raw second-highest score.',
        },
      },
    };
  }

  function roundScore(value) {
    return Math.round(value * 1000) / 1000;
  }

  function mapValues(obj, fn) {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(value)]));
  }

  const LsdnQuizEngine = {
    calculateQuizResults,
    recordQuizPair,
    getRecentPairs,
    // Exposed for testing / debugging
    _internal: {
      normalizeAnswers,
      buildScoreState,
      selectPrimary,
      selectSecondary,
      compareRankedEntries,
      getUsageType,
    },
  };

  global.LsdnQuizEngine = LsdnQuizEngine;
  global.calculateQuizResults = calculateQuizResults;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = LsdnQuizEngine;
  }
})(typeof window !== 'undefined' ? window : globalThis);
